import { useEffect, useRef, type MutableRefObject } from 'react';
import { useFramework } from '@/framework';
import { type IPoint } from '@/version-types';
import type { TextEditorPlugin } from '../manager';
import { getTextRangeFromPoint } from '../utils';
import { type Ref as TextEditorGeometryRef } from './geometry';
import { useSession } from './use-session';

type Props = {
    /**
     * 文本编辑几何视图引用。
     *
     * 图层双击事件先于编辑视图挂载发生，因此需要暂存双击坐标；待 session 建立且几何视图挂载后，
     * 再通过此引用把页面坐标转换为编辑器坐标，以还原初始光标位置。
     */
    geometryRef: MutableRefObject<TextEditorGeometryRef | null>;

    /**
     * 结束当前编辑会话的统一入口。
     *
     * 该回调由编辑视图提供，用于在插件退出前先提交 textarea 中尚未同步的内容和 IME 组合输入；
     * 双击切换图层时还需要等待退出完成，避免新旧编辑会话交叠。
     */
    exitEditing: () => Promise<void> | void;
};

/**
 * 管理文本编辑会话的外部生命周期：进入、退出、图层同步和退出前输入法提交。
 *
 * 几何坐标转换和 textarea 输入提交由对应视图持有，因此通过显式 ref 与回调注入，
 * 保证生命周期处理可以使用已挂载的几何视图，并按顺序等待输入提交和会话退出。
 *
 * @param props - 生命周期依赖，包括用于转换双击坐标的 geometryRef，以及提交输入后退出会话的 exitEditing。
 * @returns 无返回值。
 */
export const useLifecycle = ({ geometryRef, exitEditing }: Props) => {
    const framework = useFramework();
    const textEditor = framework.service.registration.getInstance<TextEditorPlugin>('TextEditorPlugin');

    const session = useSession();

    /**
     * 暂存的双击坐标，用于在编辑会话创建后用以还原初始文本选区。
     */
    const initialPointRef = useRef<IPoint | null>(null);

    /**
     * 双击文本图层时进入编辑，并暂存双击坐标供选区定位使用。
     */
    useEffect(() => {
        const handler = framework.service.frame.onLayerDoubleClick((layerID, point) => {
            const layer = framework.service.flatLayer.getLayer(layerID, true);
            if (!layer) return;

            initialPointRef.current = point;
            void (async () => {
                if (textEditor.getSession()) await exitEditing();
                if (!(await textEditor.enter(layer.id))) initialPointRef.current = null;
            })();
        });
        return () => framework.service.events.off(handler);
    }, [exitEditing, framework, textEditor]);

    /**
     * 监听外部图层变化；图层失效、锁定、只读或历史变化时退出编辑。
     */
    useEffect(() => {
        const clickHandler = framework.service.frame.onLayerClick((layerID) => {
            const current = textEditor.getSession();
            if (current && current.editingLayer.id !== layerID) exitEditing();
        });

        const activeHandler = framework.service.frame.onActiveLayerDataUpdate((activeLayer, pickLayer) => {
            const current = textEditor.getSession();
            if (!current) return;

            const layer = pickLayer || activeLayer;
            if (!layer || layer.id !== current.editingLayer.id) return exitEditing();
            if (layer.lock) return exitEditing();

            textEditor.syncLayer(layer);
        });

        const readonlyHandler = framework.service.editor.onReadonlyChange((flag) => {
            if (flag) exitEditing();
        });

        const historyHandler = framework.service.history.onBeforeChange(() => exitEditing());

        return () => {
            framework.service.events.off(clickHandler);
            framework.service.events.off(activeHandler);
            framework.service.events.off(readonlyHandler);
            framework.service.events.off(historyHandler);
        };
    }, [exitEditing, framework, textEditor]);

    /**
     * 编辑会话创建后，用暂存的双击坐标还原初始文本选区。
     */
    useEffect(() => {
        if (!session) return;

        const point = initialPointRef.current;
        if (!point) return;

        // 保留进入会话时的全选，避免双击坐标将其覆盖成光标。
        if (session.selection.start !== session.selection.end) {
            initialPointRef.current = null;
            return;
        }

        const svgPoint = geometryRef.current?.getPointFromPage(point);
        if (!svgPoint) return;

        initialPointRef.current = null;

        const range = getTextRangeFromPoint(session.snapshot, svgPoint);
        textEditor.setSelection(range);
    }, [geometryRef, session, textEditor]);
};
